/**
 * Registry contracts of the Primes components: Zod `propsSchema`s of their
 * data props (checked against the TypeScript props with `satisfies`), the
 * admin prop labels and the preview samples.
 *
 * Host-wired callbacks (`onOpenDocuments`, `onFiltersChange`, `onClose`) and the injected
 * `_layoutOverrides` are deliberately absent: rules target the stable
 * column / filter / item / action keys through `_layoutOverrides`, never the
 * callbacks.
 */

import { z } from 'zod';

import type { Premium } from '../../api/premiumsService';
import { documentItemSchema } from '../shared';
import type { PremiumDocumentsPanelProps } from './PremiumDocumentsPanel';
import type { PremiumsListCardProps } from './PremiumsListCard';

// ---------------------------------------------------------------------------
// extranet.premiumsListCard
// ---------------------------------------------------------------------------

/** Props of the list tracked by the registry. */
export type PremiumsListCardDataProps = Pick<PremiumsListCardProps, 'premiums' | 'loading' | 'error'>;

export const premiumSchema = z.object({
  id: z.string(),
  idQui: z.number().nullable(),
  assure: z.string().nullable(),
  produit: z.string().nullable(),
  numeroContrat: z.string().nullable(),
  reference: z.string().nullable(),
  periodeDu: z.union([z.number(), z.string()]).nullable(),
  periodeAu: z.union([z.number(), z.string()]).nullable(),
  ttc: z.number().nullable(),
  situation: z.string().nullable(),
}) satisfies z.ZodType<Premium>;

export const premiumsListCardDataSchema = z.object({
  premiums: z.array(premiumSchema),
  loading: z.boolean().optional(),
  error: z.string().nullable().optional(),
}) satisfies z.ZodType<PremiumsListCardDataProps>;

export const PREMIUMS_LIST_PROP_LABELS = {
  premiums: 'Primes',
  loading: 'Chargement en cours',
  error: "Message d'erreur",
} as const;

export const PREMIUMS_LIST_PREVIEW_PROPS: PremiumsListCardDataProps = {
  premiums: [
    {
      id: '44293',
      idQui: 44293,
      assure: 'Atelier Voltaire SARL',
      produit: 'Multirisque Professionnelle',
      numeroContrat: 'MRP-2024-018745',
      reference: 'QUI-2024-08-001',
      periodeDu: 1711922400000,
      periodeAu: 1743372000000,
      ttc: 4820,
      situation: 'Payée',
    },
    {
      id: '44294',
      idQui: 44294,
      assure: 'Logistique Garonne SAS',
      produit: 'Flotte Automobile',
      numeroContrat: 'FLT-2024-002311',
      reference: 'QUI-2025-Q1',
      periodeDu: 1735686000000,
      periodeAu: 1743372000000,
      ttc: 7112,
      situation: 'Impayée',
    },
  ],
};

// ---------------------------------------------------------------------------
// extranet.premiumDocumentsPanel
// ---------------------------------------------------------------------------

/** Props of the panel tracked by the registry (`onClose` is page-wired). */
export type PremiumDocumentsPanelDataProps = Pick<
  PremiumDocumentsPanelProps,
  'open' | 'idQui' | 'numQui' | 'documents' | 'loading' | 'error'
>;

export const premiumDocumentsPanelDataSchema = z.object({
  open: z.boolean(),
  idQui: z.number().nullable(),
  numQui: z.string().nullable(),
  documents: z.array(documentItemSchema),
  loading: z.boolean().optional(),
  error: z.string().nullable().optional(),
}) satisfies z.ZodType<PremiumDocumentsPanelDataProps>;

export const PREMIUM_DOCUMENTS_PANEL_PROP_LABELS = {
  open: 'Panneau ouvert',
  idQui: 'Identifiant de la prime (ID_QUI)',
  numQui: 'Référence de la prime (NUM_QUI)',
  documents: 'Documents',
  loading: 'Chargement en cours',
  error: "Message d'erreur",
} as const;

/** Preview sample — no `onClose`, so the panel renders inline (see the component). */
export const PREMIUM_DOCUMENTS_PANEL_PREVIEW_PROPS: PremiumDocumentsPanelDataProps = {
  open: true,
  idQui: 44293,
  numQui: 'QUI-2024-08-001',
  documents: [
    { id: '210741', nom: 'Quittance 2024-2025.pdf', categorie: 'Quittance', date: '15/04/2024' },
    { id: '210742', nom: "Avis d'échéance.pdf", categorie: 'Avis', date: '15/03/2024' },
  ],
};
